/**
 * AUTH_SECRET Resolver
 *
 * Resolves AUTH_SECRET: env → DB (single_records) → auto-generate + persist.
 * Race-safe for PM2 cluster: INSERT OR IGNORE / ON CONFLICT DO NOTHING.
 */

import { randomBytes } from 'node:crypto'
import type { Knex } from 'knex'
import { getDatabaseType } from '@kernel/config/database.js'

const SINGLE_RECORDS = 'single_records'
const SECRET_KEY = 'nexus_auth_secret'
const SECRET_LENGTH = 48 // 48 bytes → 64 chars base64url

let _secret: string | null = null
let _db: Knex | null = null

/** Set DB reference for secret persistence. Called from server.ts after DB ready. */
export function setSecretResolverDb(db: Knex): void {
  _db = db
}

/** Reset state (for testing and server stop). */
export function _resetSecretResolver(): void {
  _secret = null
  _db = null
}

/**
 * Initialize the auth secret. Must be called once during startup (after DB ready).
 *
 * Resolution order:
 * 1. process.env.AUTH_SECRET (explicit config always wins)
 * 2. single_records in DB (persisted auto-generated secret)
 * 3. Generate new → persist → use
 */
export async function initAuthSecret(logger: { info: (msg: string) => void; warn: (msg: string) => void }): Promise<void> {
  // 1. ENV takes priority
  const envSecret = process.env['AUTH_SECRET']
  if (envSecret) {
    if (envSecret.length < 32) {
      throw new Error(`AUTH_SECRET must be at least 32 characters (current: ${envSecret.length})`)
    }
    _secret = envSecret
    return
  }

  if (!_db) {
    throw new Error('Secret resolver: database not initialized. Call setSecretResolverDb() first.')
  }

  // 2. Try DB
  const existing = await _db(SINGLE_RECORDS).where('key', SECRET_KEY).first()
  if (existing?.value) {
    const parsed = typeof existing.value === 'string' ? JSON.parse(existing.value) : existing.value
    if (parsed.secret) {
      _secret = parsed.secret
      logger.info('AUTH_SECRET loaded from database')
      warnIfProduction(logger)
      return
    }
  }

  // 3. Generate + persist (race-safe)
  const generated = randomBytes(SECRET_LENGTH).toString('base64url')
  const value = JSON.stringify({ secret: generated })
  const id = randomBytes(13).toString('base64url')
  const now = new Date()
  const dialect = getDatabaseType()

  if (dialect === 'postgresql') {
    await _db.raw(
      `INSERT INTO ${SINGLE_RECORDS} (id, key, value, created_at, updated_at) VALUES (?, ?, ?, ?, ?) ON CONFLICT (key) DO NOTHING`,
      [id, SECRET_KEY, value, now, now]
    )
  } else if (dialect === 'mysql') {
    await _db.raw(
      'INSERT IGNORE INTO `single_records` (id, `key`, value, created_at, updated_at) VALUES (?, ?, ?, ?, ?)',
      [id, SECRET_KEY, value, now, now]
    )
  } else {
    // SQLite
    await _db.raw(
      `INSERT OR IGNORE INTO ${SINGLE_RECORDS} (id, key, value, created_at, updated_at) VALUES (?, ?, ?, ?, ?)`,
      [id, SECRET_KEY, value, now, now]
    )
  }

  // Re-read (the winner of the race)
  const persisted = await _db(SINGLE_RECORDS).where('key', SECRET_KEY).first()
  const parsed = typeof persisted.value === 'string' ? JSON.parse(persisted.value) : persisted.value
  _secret = parsed.secret

  logger.info('AUTH_SECRET auto-generated and persisted in database')
  warnIfProduction(logger)
}

function warnIfProduction(logger: { warn: (msg: string) => void }): void {
  if (process.env['NODE_ENV'] === 'production') {
    logger.warn('AUTH_SECRET not set via environment. Using auto-generated secret from database. For multi-instance deployments, set AUTH_SECRET explicitly.')
  }
}

/**
 * Get the resolved secret.
 *
 * Falls back to process.env.AUTH_SECRET if resolver was not initialized
 * (backward compat for tests that set env directly without full startup).
 */
export function getAuthSecret(): string {
  if (_secret) return _secret
  // Fallback: env without init (backward compat for tests)
  const envSecret = process.env['AUTH_SECRET']
  if (envSecret) return envSecret
  throw new Error('AUTH_SECRET not initialized. Call initAuthSecret() during startup.')
}
